import { req } from "./transport";
import type { AgreementDocument, BusinessQuoteSource, BusinessSaleRequirements, ServiceQuoteInput, ServiceQuote, RfqItemProgress, RfqItemQuoteInput, RfqItemQuote } from "./types";

export const quotationsApi = {
  // Increment 7: RFQ + itemized quotes
  async getRfq(id: string) {
    const r = await req<{ item: any }>(`/marketplace/rfqs/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },

  async getQuotes(rfqId: string) {
    return req<{
      isOwner: boolean; total: number; status: string; awardedQuoteId: string | null;
      deadlineExpired?: boolean; deadlineAt?: number | null; canSubmitQuotation?: boolean; canReviseQuotation?: boolean;
      items: any[]; quotes: any[]; myQuote?: any | null;
    }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes`);
  },

  async submitQuote(rfqId: string, payload: Record<string, unknown>) {
    return req<{ quote: any; total: number }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes`, { method: "POST", body: JSON.stringify(payload) });
  },

  async shortlistQuote(rfqId: string, qid: string) {
    return req<{ quote: { id: string; status: string } }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes/${encodeURIComponent(qid)}/shortlist`, { method: "POST" });
  },

  async awardQuote(rfqId: string, qid: string, opts?: { milestones?: { label: string; amount: number }[]; scopeOfWork?: string }) {
    const body: any = {};
    if (opts?.milestones) body.milestones = opts.milestones;
    if (opts?.scopeOfWork) body.scopeOfWork = opts.scopeOfWork;
    return req<{ ok: boolean; awardedQuoteId: string; status: string; purchaseOrder: any }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes/${encodeURIComponent(qid)}/award`, { method: "POST", body: JSON.stringify(body) });
  },

  async myQuotes(signal?: AbortSignal) {
    return req<{ quotes: any[] }>("/rfqs/mine/quotes", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async getRfqItemProgress(rfqId: string) {
    return req<{ items: RfqItemProgress[]; status: string }>(`/rfqs/${encodeURIComponent(rfqId)}/item-progress`);
  },

  async getRfqItemQuotes(rfqId: string) {
    return req<{ isOwner: boolean; canQuote: boolean; deadlineExpired?: boolean; deadlineAt?: number | null; canSubmitQuotation?: boolean; canReviseQuotation?: boolean; quotes: RfqItemQuote[] }>(`/rfqs/${encodeURIComponent(rfqId)}/item-quotes`);
  },

  async sendRfqItemQuote(rfqId: string, quote: RfqItemQuoteInput) {
    return req<{ threadId: string; quote: RfqItemQuote }>(`/rfqs/${encodeURIComponent(rfqId)}/item-quotes`, { method: "POST", body: JSON.stringify(quote) });
  },

  async previewRfqItemQuote(rfqId: string, quote: RfqItemQuoteInput) {
    return req<{ document: AgreementDocument | null }>(`/rfqs/${encodeURIComponent(rfqId)}/item-quotes/preview`, { method: "POST", body: JSON.stringify(quote) });
  },

  async previewRfqItemSelection(quote: RfqItemQuote, itemIds: string[]) {
    return req<{ document: AgreementDocument | null }>(`/rfqs/${encodeURIComponent(quote.rfqId)}/item-quotes/${encodeURIComponent(quote.id)}/selection-preview`, { method: "POST", body: JSON.stringify({ itemIds }) });
  },

  async acceptRfqItemQuote(quote: RfqItemQuote, itemIds: string[], expectedDocumentId?: string) {
    return req<{ contractId: string; replay: boolean }>(`/rfqs/${encodeURIComponent(quote.rfqId)}/item-quotes/${encodeURIComponent(quote.id)}/accept`, { method: "POST", body: JSON.stringify({ itemIds, expectedDocumentId }) });
  },

  async reviewRfqItemQuote(quote: RfqItemQuote, decision: "changes" | "decline", note: string) {
    return req<{ ok: boolean }>(`/rfqs/${encodeURIComponent(quote.rfqId)}/item-quotes/${encodeURIComponent(quote.id)}/${decision}`, { method: "POST", body: JSON.stringify({ note }) });
  },

  async previewRfqServiceQuote(rfqId: string, quote: ServiceQuoteInput) {
    return req<{ document: AgreementDocument | null }>(`/rfqs/${encodeURIComponent(rfqId)}/service-quotes/preview`, { method: "POST", body: JSON.stringify(quote) });
  },

  async businessSaleRequirements(source: BusinessQuoteSource) {
    return req<BusinessSaleRequirements>(`/threads/${encodeURIComponent(source.threadId)}/service-quotes/business-requirements?${new URLSearchParams({ businessItemId: source.businessItemId, requestMessageId: source.requestMessageId })}`);
  },

  async previewBusinessQuote(source: BusinessQuoteSource, quote: ServiceQuoteInput) {
    return req<{ document: AgreementDocument | null }>(`/threads/${encodeURIComponent(source.threadId)}/service-quotes/preview`, { method: "POST", body: JSON.stringify({ ...quote, businessItemId: source.businessItemId, requestMessageId: source.requestMessageId }) });
  },

  async previewServiceQuote(threadId: string, serviceId: string, requestMessageId: string, quote: ServiceQuoteInput) {
    return req<{ document: AgreementDocument | null }>(`/threads/${encodeURIComponent(threadId)}/service-quotes/preview`, { method: "POST", body: JSON.stringify({ ...quote, serviceId, requestMessageId }) });
  },

  async sendRfqServiceQuote(rfqId: string, quote: ServiceQuoteInput) {
    return req<{ threadId: string; quote: ServiceQuote }>(`/rfqs/${encodeURIComponent(rfqId)}/service-quotes`, { method: "POST", body: JSON.stringify(quote) });
  },

  async sendServiceQuote(threadId: string, serviceId: string | null, requestMessageId: string, quote: ServiceQuoteInput, businessItemId?: string | null) {
    return req<{ quote: ServiceQuote }>(`/threads/${encodeURIComponent(threadId)}/service-quotes`, { method: "POST", body: JSON.stringify({ ...quote, serviceId: serviceId || undefined, businessItemId: businessItemId || undefined, requestMessageId }) });
  },

  async requestServiceQuoteChanges(threadId: string, quoteId: string, note: string) {
    return req<{ ok: boolean }>(`/threads/${encodeURIComponent(threadId)}/service-quotes/${encodeURIComponent(quoteId)}/request-changes`, { method: "POST", body: JSON.stringify({ note }) });
  },

  async acceptServiceQuote(threadId: string, quoteId: string, expectedDocumentId?: string) {
    return req<{ contractId: string }>(`/threads/${encodeURIComponent(threadId)}/service-quotes/${encodeURIComponent(quoteId)}/accept`, { method: "POST", body: JSON.stringify({ expectedDocumentId }) });
  }
};
